/**
 * DefaultProcessCell — komórka „Proces domyślny" w tabeli typów rekordu (typ dokumentu, typ zadania).
 *
 * Edytowalna: lista wyboru w miejscu („— brak —" + opublikowane procesy kartoteki), zmiana woła
 * `onChange(id | null)` od razu; błąd zapisu pokazuje wołający (toast — lista nie ma własnej
 * powierzchni błędu). Tylko do odczytu: nazwa procesu albo „—".
 */

import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import type { DefaultProcessOption } from '@/lib/record-types/default-process';

export interface DefaultProcessCellProps {
  processes: DefaultProcessOption[];
  /** Wskazany proces; `null` = brak. */
  value: string | null;
  /** `true` = lista wyboru w miejscu; `false` = sam tekst. */
  canEdit: boolean;
  /** Zapis wskazania trwa — lista zablokowana, żeby nie wysłać drugiej zmiany w międzyczasie. */
  pending?: boolean;
  onChange: (processId: string | null) => void;
  'data-testid'?: string;
}

export function DefaultProcessCell({
  processes,
  value,
  canEdit,
  pending = false,
  onChange,
  'data-testid': testId,
}: DefaultProcessCellProps) {
  const { t } = useTranslation();
  if (!canEdit) {
    return (
      <>
        {processes.find((p) => p.id === value)?.name ?? <span className="text-text-muted">—</span>}
      </>
    );
  }
  return (
    <Dropdown
      value={value ?? ''}
      onChange={(v) => onChange(v || null)}
      disabled={pending}
      data-testid={testId}
      options={[
        { value: '', label: t('tenant.recordTypes.defaultProcess.none') },
        ...processes.map((p) => ({ value: p.id, label: p.name })),
      ]}
    />
  );
}
